import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getPublicReceipt, formatMXN, API } from "@/lib/api";
import { Sparkles } from "lucide-react";

function fmtMx(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const PAYMENT_LABEL = { cash: "Efectivo", card: "Tarjeta", credit: "Fiado" };

export default function Receipt() {
  const { saleId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getPublicReceipt(saleId).then(setData).catch(() => setErr("Ticket no encontrado"));
  }, [saleId]);

  if (err) {
    return (
      <main className="min-h-screen bg-[#f5f2ea] text-neutral-900 flex items-center justify-center p-6" data-testid="receipt-error">
        <div className="text-center">
          <p className="font-mono text-xs uppercase tracking-widest text-neutral-500">Recibo digital</p>
          <h1 className="font-display text-3xl mt-2">{err}</h1>
        </div>
      </main>
    );
  }
  if (!data) return <main className="min-h-screen bg-[#f5f2ea] flex items-center justify-center text-neutral-500 text-sm">Cargando ticket…</main>;

  const s = data.sale;
  return (
    <main className="min-h-screen bg-[#f5f2ea] text-neutral-900 p-4 md:p-8 flex justify-center" data-testid="receipt-page">
      <div className="w-full max-w-md bg-[#fefaf1] shadow-[0_25px_80px_-25px_rgba(0,0,0,0.35)] rounded-lg overflow-hidden font-mono">
        <div className="h-3" style={{ backgroundImage: "radial-gradient(circle at 6px 6px, transparent 6px, #f5f2ea 6.5px)", backgroundSize: "12px 12px", backgroundRepeat: "repeat-x" }} />
        <div className="px-6 py-5">
          <div className="text-center pb-3 border-b border-dashed border-black/20">
            <p className="text-[10px] tracking-[0.3em] text-black/60 uppercase">Ticket digital</p>
            <p className="font-display text-2xl tracking-tighter mt-1" data-testid="receipt-store">{data.store_name}</p>
            <p className="text-[11px] text-black/60 mt-1" data-testid="receipt-datetime">{fmtMx(s.created_at)}</p>
            <p className="text-[10px] text-black/40 mt-1">Folio · {s.id.slice(0, 8).toUpperCase()}</p>
          </div>

          <div className="pt-4 space-y-1.5 text-[12px]">
            {s.items.map((it, i) => {
              const isBulk = (it.unit === "kg") || it.name?.toLowerCase().includes("granel");
              const qtyLabel = isBulk
                ? `${Number(it.quantity).toFixed(3)} kg`
                : `${it.quantity}`;
              return (
                <div key={i} className="flex justify-between gap-2" data-testid={`receipt-item-${i}`}>
                  <span className="truncate pr-2">
                    <span className="text-black/70">{qtyLabel}×</span> {it.name}
                  </span>
                  <span>{formatMXN(it.subtotal)}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-3 border-t border-dashed border-black/20 space-y-1 text-[12px]">
            <div className="flex justify-between font-black text-lg pt-1">
              <span>TOTAL</span>
              <span data-testid="receipt-total">{formatMXN(s.total_amount)}</span>
            </div>
            <div className="flex justify-between text-black/70 pt-1">
              <span>Pago</span><span data-testid="receipt-payment">{PAYMENT_LABEL[s.payment_method] || s.payment_method}</span>
            </div>
            {s.customer_name && (
              <div className="flex justify-between text-black/60"><span>Cliente</span><span>{s.customer_name}</span></div>
            )}
            <div className="flex justify-between text-black/60"><span>Cajero</span><span>{s.cashier_name || "Dueño"}</span></div>
          </div>

          <div className="text-center mt-5 pt-4 border-t border-dashed border-black/20">
            <p className="font-display text-base tracking-tight">
              ¡Gracias por tu compra en {data.store_name}!
            </p>
            <p className="text-[10px] text-black/50 mt-2 flex items-center justify-center gap-1">
              <Sparkles size={10} /> Impulsado por IA · CHALANN
            </p>
          </div>
        </div>
        <div className="h-3" style={{ backgroundImage: "radial-gradient(circle at 6px 6px, transparent 6px, #f5f2ea 6.5px)", backgroundSize: "12px 12px", backgroundRepeat: "repeat-x" }} />
      </div>
      {/* Silence unused import lint */}
      <span className="hidden" data-api={API} />
    </main>
  );
}
